import { openComposeWithViewState, prepareComposeViewState } from './openCompose';
import trySaveAndCloseCompose from './trySaveAndCloseCompose';
import loadItemLabelBeforeCompose from '../utils/loadItemLabelBeforeCompose';
import { saveWithAction } from '../actions/trySaveMessage';
import upgradeCompose from '../actions/upgradeCompose';
import datapoints from '../datapoints';
import { ComposeViewState, ComposeOperation, ComposeTarget } from 'owa-mail-compose-store';
import { ComposeViewStateInitProps } from '../utils/createComposeViewState';
import { createReplyFromReplyItem, createReplyItem } from '../utils/createReply';
import findInlineComposeViewState from '../utils/findInlineComposeViewState';
import getCLPLabelExtendedPropertyValue from 'owa-mail-clp/lib/utils/getCLPLabelExtendedPropertyValue';
import getClassificationFromMessage from 'owa-mail-message-classification/lib/utils/getClassificationFromMessage';
import getItemWithNormalizedBody from '../utils/getItemWithNormalizedBody';
import getQuotedBodyForSmimeItemReply from '../utils/getQuotedBodyForSmimeItemReply';
import { logReplyInfo } from '../utils/logReplyInfo';
import tryGetSmimeViewState from '../utils/tryGetSmimeViewState';
import { lazyShouldAddinsForceFullCompose } from 'owa-addins-core';
import { logUsage } from 'owa-analytics';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { AttachmentFileAttributes } from 'owa-attachment-file-types';
import { isSmimeAttachment } from 'owa-attachment-model-store';
import { ClientItemId, MailboxType } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getGuid } from 'owa-guid';
import { userMailInteractionAction } from 'owa-mail-actions';
import isCLPEnabled from 'owa-mail-clp/lib/utils/isCLPEnabled';
import { ClientItem } from 'owa-mail-store';
import { getStore, InstrumentationContext } from 'owa-mail-store';
import { isDeepLink } from 'owa-popout';
import Message from 'owa-service/lib/contract/Message';
import { lazyGetAttachmentsForSmimeResponse } from 'owa-smime';
import SmimeErrorStateEnum from 'owa-smime-adapter/lib/store/schema/SmimeErrorStateEnum';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import isSMIMEItem from 'owa-smime/lib/utils/isSMIMEItem';
import { isSxSDisplayed } from 'owa-sxs-store';
import { trace } from 'owa-trace';
import { mutatorAction } from 'satcheljs';

export interface ReplyToMessageCommonOptions {
    isReplyAll: boolean;
    useFullCompose: boolean;
    actionSource?: ActionSource;
    fullComposeTarget?: ComposeTarget;
    attachmentFiles?: AttachmentFileAttributes[];
    conversationId?: string;
    instrumentationContexts: InstrumentationContext[];
    initialComposeLogTraceId?: string;
    body?: string;
    groupId?: string;
    suppressServerMarkReadOnReplyOrForward?: boolean;
}

export interface ReplyToMessageAndCreateViewStateOptions extends ReplyToMessageCommonOptions {
    referenceItemOrId: string | Message;
}

export interface ReplyToMessageActionOptions extends ReplyToMessageCommonOptions {
    referenceItemId: string;
    onComposeViewStateCreated?: (newComposeViewState: ComposeViewState) => void;
}

// Remove groupId from ReplyToMessageCommonOptions and add replyToMessage-specific properties
export type ReplyToMessageOptions = Pick<
    ReplyToMessageCommonOptions,
    Exclude<keyof ReplyToMessageCommonOptions, 'groupId'>
> & {
    referenceItemId: ClientItemId;
    onComposeViewStateCreated?: (newComposeViewState: ComposeViewState) => void;
};

export async function replyToMessageAndCreateViewState(
    options: ReplyToMessageAndCreateViewStateOptions
): Promise<ComposeViewState> {
    let viewState: ComposeViewState = null;
    let fullComposeTarget = options.fullComposeTarget;
    const {
        referenceItemOrId,
        isReplyAll,
        useFullCompose,
        conversationId,
        initialComposeLogTraceId,
        groupId,
        body,
        instrumentationContexts,
        attachmentFiles,
        actionSource,
        suppressServerMarkReadOnReplyOrForward,
    } = options;

    if (fullComposeTarget === undefined) {
        fullComposeTarget = isSxSDisplayed()
            ? ComposeTarget.SxS
            : isDeepLink()
            ? ComposeTarget.PrimaryReadingPane
            : ComposeTarget.SecondaryTab;
    }

    if (!useFullCompose) {
        const existingViewState = findInlineComposeViewState(conversationId);
        if (existingViewState) {
            await trySaveAndCloseCompose(existingViewState);

            // If there is still default compose viewstate, it means it is failed to save and close previous compose
            // So no-op here
            if (findInlineComposeViewState(conversationId)) {
                return Promise.resolve(null);
            }
        }
    }

    // If traceId is not passed create new one so we can correlate between all replies and sent
    const composeTraceId = initialComposeLogTraceId || getGuid();

    let referenceItem: Message;

    if (typeof referenceItemOrId === 'string') {
        const mailStore = getStore();
        const items = mailStore && mailStore.items;

        if (items && items.has(referenceItemOrId)) {
            referenceItem = items.get(referenceItemOrId) as Message;
        }
    } else {
        referenceItem = referenceItemOrId as Message;
    }

    if (referenceItem) {
        // create a reply item so we can initialize composeInitProps from it
        // before creating the reply.
        const replyItem = await createReplyItem(referenceItem, isReplyAll);
        const composeOperation = isReplyAll ? ComposeOperation.ReplyAll : ComposeOperation.Reply;
        const smimeViewState = await tryGetSmimeViewState(
            composeOperation /* isReplyOrReplyAll */,
            referenceItem as ClientItem
        );

        let existingLabelString = '';
        if (isCLPEnabled()) {
            existingLabelString = getCLPLabelExtendedPropertyValue(referenceItem);
        }

        let classification = null;
        if (isFeatureEnabled('cmp-message-classification')) {
            classification = getClassificationFromMessage(referenceItem);
        }

        const commonComposeInitProps: ComposeViewStateInitProps = {
            operation: composeOperation,
            bodyType: replyItem.NewBodyContent.BodyType,
            subject: replyItem.Subject,
            newContent: body,
            to: replyItem.ToRecipients,
            cc: replyItem.CcRecipients,
            bcc: replyItem.BccRecipients,
            referenceItemId: referenceItem.ItemId,
            classification,
        };

        /**
         * Below check decides whether the replying mail will be an S/MIME mail
         * or not. This condition has to be first one in the below if..elseIf blocks
         * because if S/MIME admin settings are enabled then by default every reply mail will
         * be an S/MIME mail. Also, if the message is an S/MIME mail or an attachment of an S/MIME mail, we need to
         * ensure that we treat it like a S/MIME mail and not make the server call since server has no info about this mail.
         */
        if (
            !groupId &&
            isFeatureEnabled('cmp-smime') &&
            (isSmimeAttachment(referenceItem.ItemId.Id) ||
                isSMIMEItem(referenceItem) ||
                isSmimeEnabledInViewState(smimeViewState))
        ) {
            /**
             * Fetch Item from server in case normalized body is missing.
             */
            if (!referenceItem.NormalizedBody) {
                try {
                    referenceItem = await getItemWithNormalizedBody(referenceItem.ItemId.Id);
                } catch (error) {
                    trace.error(
                        `SMIME: The reference item's NormalizedBody cannot be retrieved while replying. ${error}`
                    );
                    smimeViewState.errorCode = SmimeErrorStateEnum.QuotedBodyIsNotComplete;
                }
            }

            const quotedBody = await getQuotedBodyForSmimeItemReply(
                referenceItem,
                composeOperation,
                replyItem.NewBodyContent.BodyType === 'HTML'
            );
            const composeInitProps: ComposeViewStateInitProps = {
                ...commonComposeInitProps,
                referenceItemDocumentId: replyItem.ReferenceItemDocumentId,
                isInlineCompose: false /** Smime reply is not supported in inline compose */,
                preferAsyncSend: false,
                useSmartResponse: false,
                conversationId: conversationId,
                logTraceId: composeTraceId,
                IRMData: referenceItem.RightsManagementLicenseData,
                quotedBody,
                attachments: (await lazyGetAttachmentsForSmimeResponse.import())(
                    referenceItem.Attachments,
                    referenceItem.ItemId,
                    false /* isForward */
                ),
                smimeViewState,
            };

            viewState = await prepareComposeViewState(
                composeInitProps,
                null /* groupId */,
                attachmentFiles
            );
            openComposeWithViewState(viewState, fullComposeTarget);
        } else if (conversationId && !useFullCompose) {
            const composeInitProps: ComposeViewStateInitProps = {
                ...commonComposeInitProps,
                referenceItemDocumentId: replyItem.ReferenceItemDocumentId,
                conversationId: conversationId,
                preferAsyncSend: true,
                IRMData: referenceItem.RightsManagementLicenseData,
                logTraceId: composeTraceId,
                smimeViewState,
                existingLabelString: existingLabelString,
            };

            viewState = await prepareComposeViewState(composeInitProps, groupId, attachmentFiles);
            openComposeWithViewState(viewState, ComposeTarget.PrimaryReadingPane);

            if (!groupId) {
                // Create the draft in background to prepare for upConvert
                saveWithAction(
                    viewState,
                    createReplyFromReplyItem(
                        replyItem,
                        null,
                        suppressServerMarkReadOnReplyOrForward
                    ).then(message => {
                        if (message) {
                            updateViewStateAfterReplyCreated(viewState, message);
                        }
                    })
                );
            }
        } else {
            const composeInitProps: ComposeViewStateInitProps = {
                ...commonComposeInitProps,
                preferAsyncSend: true,
                IRMData: referenceItem.RightsManagementLicenseData,
                useSmartResponse: true, // Set useSmartResponse to true in case we can't get quoted body
                logTraceId: composeTraceId,
                smimeViewState,
                isInlineCompose: false,
                conversationId: referenceItem.ConversationId
                    ? referenceItem.ConversationId.Id
                    : null,
                existingLabelString: existingLabelString,
            };

            const useUpgradeCompose = isFeatureEnabled('cmp-upgradeCompose');

            if (!useUpgradeCompose || fullComposeTarget == ComposeTarget.Popout) {
                const message = await createReplyFromReplyItem(
                    replyItem,
                    groupId,
                    suppressServerMarkReadOnReplyOrForward
                );
                if (message) {
                    composeInitProps.itemId = message.ItemId;
                    composeInitProps.quotedBody = message.Body.Value;
                    composeInitProps.attachments = message.Attachments;
                    composeInitProps.useSmartResponse = false; // Quoted body is loaded, no need to use smart response now
                }
            }

            viewState = await prepareComposeViewState(composeInitProps, groupId, attachmentFiles);

            openComposeWithViewState(viewState, fullComposeTarget);

            if (useUpgradeCompose && fullComposeTarget != ComposeTarget.Popout) {
                upgradeCompose(
                    viewState,
                    createReplyFromReplyItem(
                        replyItem,
                        groupId,
                        suppressServerMarkReadOnReplyOrForward
                    )
                );
            }
        }

        if (viewState) {
            loadItemLabelBeforeCompose(existingLabelString, referenceItem, viewState.composeId);
        }

        logReplyInfo(replyItem, composeTraceId, actionSource, referenceItem.DateTimeReceived);

        let triageContext = null;
        if (replyItem.ToRecipients.length == 1) {
            const recipient = replyItem.ToRecipients[0];
            const senderSmtp = recipient.EmailAddress;
            const displayName = recipient.Name;
            triageContext = {
                uniqueSenders: [displayName],
                lastSenderSmtpAddress: senderSmtp,
            };
        }
        isReplyAll
            ? userMailInteractionAction('ReplyAll', instrumentationContexts)
            : userMailInteractionAction('Reply', instrumentationContexts, triageContext);
    }

    return viewState;
}

// We need to have two entrance for reply and replyAll since they are using different datapoint name
const replyToMessageAction = createLegacyActionWithDatapoint(
    'replyToMessage',
    datapoints.MailComposeReply
)(async function replyToMessageAction(options: ReplyToMessageActionOptions) {
    const composeViewState: ComposeViewState = await replyToMessageAndCreateViewState({
        ...(options as ReplyToMessageCommonOptions),
        referenceItemOrId: options.referenceItemId,
        isReplyAll: false,
    });

    if (options.onComposeViewStateCreated) {
        options.onComposeViewStateCreated(composeViewState);
    }

    if (isSxSDisplayed()) {
        logUsage('ReplyInSxS');
    }
});

const replyAllToMessageAction = createLegacyActionWithDatapoint(
    'replyAllToMessage',
    datapoints.MailComposeReplyAll
)(async function replyAllToMessageAction(options: ReplyToMessageActionOptions) {
    const composeViewState: ComposeViewState = await replyToMessageAndCreateViewState({
        ...(options as ReplyToMessageCommonOptions),
        referenceItemOrId: options.referenceItemId,
        isReplyAll: true,
    });

    if (options.onComposeViewStateCreated) {
        options.onComposeViewStateCreated(composeViewState);
    }

    if (isSxSDisplayed()) {
        logUsage('ReplyAllInSxS');
    }
});

export default async function replyToMessage(options: ReplyToMessageOptions): Promise<void> {
    const mailboxInfo = options.referenceItemId.mailboxInfo;
    const groupId =
        mailboxInfo && mailboxInfo.type == MailboxType.GroupMailbox
            ? mailboxInfo.deprecatedMailboxId
            : null;

    if (isFeatureEnabled('rp-addins')) {
        const shouldAddinsForceFullCompose = await lazyShouldAddinsForceFullCompose.import();
        if (
            (options.fullComposeTarget === ComposeTarget.PrimaryReadingPane ||
                options.fullComposeTarget === undefined) &&
            shouldAddinsForceFullCompose()
        ) {
            options.useFullCompose = true;
            options.fullComposeTarget = ComposeTarget.SecondaryTab;
        }
    }

    const parameters = {
        ...(options as ReplyToMessageCommonOptions),
        referenceItemId: options.referenceItemId.Id,
        groupId: groupId,
    };

    if (options.isReplyAll) {
        return await replyAllToMessageAction(parameters);
    } else {
        return await replyToMessageAction(parameters);
    }
}

const updateViewStateAfterReplyCreated = mutatorAction('updateViewStateAfterReplyCreated', function(
    viewState: ComposeViewState,
    message: Message
) {
    viewState.itemId = message.ItemId;
    viewState.subject = message.Subject;
    viewState.quotedBody = message.Body.Value;
});
